// Import all config files
import {
  consumersConfig,
  consumersTabDisplayNames,
  consumersTabDropdownOptions,
} from "./entities/consumer";
import {
  metersConfig,
  metersTabDisplayNames,
  metersTabDropdownOptions,
} from "./entities/meters";
import {
  consumerMeterMappingConfig,
  consumerMeterMappingTabDisplayNames,
  consumerMeterMappingTabDropdownOptions,
} from "./entities/consumerMeterMapping";
import {
  paymentsConfig,
  paymentsTabDisplayNames,
  paymentsTabDropdownOptions,
} from "./entities/payment";
import {
  assetClassificationConfig,
  assetClassificationTabDisplayNames,
  assetClassificationTabDropdownOptions,
} from "./entities/assetClassification";
import {
  systemTypesConfig,
  systemTypesTabDisplayNames,
  systemTypesTabDropdownOptions,
} from "./entities/systemTypes";
import {
  facilitiesConfig,
  facilitiesTabDisplayNames,
  facilitiesTabDropdownOptions,
} from "./entities/facility";
import {
  systemsConfig,
  systemsTabDisplayNames,
  systemsTabDropdownOptions,
} from "./entities/system";
import {
  networksConfig,
  networksTabDisplayNames,
  networksTabDropdownOptions,
} from "./entities/network";
import {
  assetsConfig,
  assetsTabDisplayNames,
  assetsTabDropdownOptions,
} from "./entities/asset";
import { DataTypeConfig } from "../../../types";

// Main configuration object containing all data types
export const dataTypeConfigs: Record<string, DataTypeConfig> = {
  consumers: consumersConfig,
  meters: metersConfig,
  "Consumer Meter Mapping": consumerMeterMappingConfig,
  payments: paymentsConfig,
  assetClassification: assetClassificationConfig,
  systemTypes: systemTypesConfig,
  facilities: facilitiesConfig,
  systems: systemsConfig,
  networks: networksConfig,
  assets: assetsConfig,
};

// Tab display names for all data types
export const tabDisplayNames: Record<string, Record<string, string>> = {
  consumers: consumersTabDisplayNames,
  meters: metersTabDisplayNames,
  "Consumer Meter Mapping": consumerMeterMappingTabDisplayNames,
  payments: paymentsTabDisplayNames,
  assetClassification: assetClassificationTabDisplayNames,
  systemTypes: systemTypesTabDisplayNames,
  facilities: facilitiesTabDisplayNames,
  systems: systemsTabDisplayNames,
  networks: networksTabDisplayNames,
  assets: assetsTabDisplayNames,
};

// Tab dropdown options for all data types
export const tabDropdownOptions: Record<
  string,
  (tab: string) => { value: string; label: string }[]
> = {
  Consumers: consumersTabDropdownOptions,
  meters: metersTabDropdownOptions,
  "Consumer Meter Mapping": consumerMeterMappingTabDropdownOptions,
  payments: paymentsTabDropdownOptions,
  assetClassification: assetClassificationTabDropdownOptions,
  systemTypes: systemTypesTabDropdownOptions,
  facilities: facilitiesTabDropdownOptions,
  systems: systemsTabDropdownOptions,
  networks: networksTabDropdownOptions,
  assets: assetsTabDropdownOptions,
};
// Main configuration function using the new modular approach
export const getDataTypeConfig = (dataType: string): DataTypeConfig => {
  return dataTypeConfigs[dataType] || dataTypeConfigs.consumers; // Default to consumers
};

export const isInvalidValue = (value: any): boolean => {
  if (!value || value === "" || value === null || value === undefined)
    return true;

  const stringValue = value.toString().toLowerCase().trim();
  const invalidPatterns = [
    "invalid",
    "unknown",
    "n/a",
    "null",
    "undefined",
    "error",
    "missing",
    "not found",
    "none",
    "blank",
  ];

  return invalidPatterns.some((pattern) => stringValue.includes(pattern));
};

// Get tab display name for all data types
export const getTabDisplayName = (
  dataType: string,
  tabName: string
): string => {
  return tabDisplayNames[dataType]?.[tabName] || tabName;
};

// Get field name for tab (database field mapping)
export const getFieldNameForTab = (
  dataType: string,
  tabName: string
): string => {
  // This function maps tab names to actual database field names
  switch (dataType) {
    case "consumers":
      switch (tabName) {
        case "plan":
          return "Plan";
        case "category":
          return "Category";
        case "subCategory":
          return "Sub Category";
        case "status":
          return "Status";
        default:
          return tabName;
      }

    case "meters":
      switch (tabName) {
        case "utilityService":
          return "utility_service";
        case "category":
          return "category";
        case "subCategory":
          return "sub_category";
        case "meterType":
          return "meter_type";
        case "meterModel":
          return "meter_model";
        case "manufacturer":
          return "manufacturer";
        default:
          return tabName;
      }

    case "Consumer Meter Mapping":
      switch (tabName) {
        case "utilityService":
          return "utility_service";
        default:
          return tabName;
      }

    case "payments":
      switch (tabName) {
        case "paymentPayType":
          return "payment_pay_type";
        case "paymentMode":
          return "payment_mode";
        case "paymentChannel":
          return "payment_channel";
        case "paymentType":
          return "payment_type";
        case "paymentSubtype":
          return "payment_subtype";
        case "status":
          return "status";
        default:
          return tabName;
      }

    case "assetClassification":
      switch (tabName) {
        case "dataType":
          return "Data Type";
        default:
          return tabName;
      }

    case "facilities":
      switch (tabName) {
        case "facilityType":
          return "Facility Type";
        case "utilityService":
          return "Utility Service";
        case "operatingStatus":
          return "Operating Status";
        case "operationalHours":
          return "Operational Hours";
        case "serviceZone":
          return "Service Zone/Division";
        case "facilityManager":
          return "Facility Manager";
        case "depreciationSchedule":
          return "Depreciation Schedule";
        default:
          return tabName;
      }

    case "systems":
      switch (tabName) {
        case "systemType":
          return "System Type";
        case "status":
          return "Status";
        case "backupStatus":
          return "Backup Status";
        case "operationalHours":
          return "Operational Hours";
        default:
          return tabName;
      }

    case "networks":
      switch (tabName) {
        case "networkType":
          return "Network Type";
        case "operationalStatus":
          return "Operational Status";
        case "networkOperator":
          return "Network Operator";
        case "backupStatus":
          return "Backup Status";
        default:
          return tabName;
      }

    case "assets":
      switch (tabName) {
        case "assetClass":
          return "Asset Class";
        case "assetType":
          return "Asset Type";
        case "manufacturer":
          return "Manufacturer";
        case "status":
          return "Status";
        case "depreciationMethod":
          return "Depreciation Method";
        default:
          return tabName;
      }

    default:
      return tabName;
  }
};

// Calculate invalid count
export const getInvalidCount = (
  data: any[],
  tabName: string,
  dataType: string
) => {
  const fieldName = getFieldNameForTab(dataType, tabName);

  const invalidRows = data.filter((row) => {
    const fieldValue = row[fieldName];
    return (
      !fieldValue ||
      fieldValue === "" ||
      fieldValue.toString().toLowerCase().startsWith("invalid") ||
      fieldValue.toString().toLowerCase() === "unknown" ||
      fieldValue.toString().toLowerCase() === "n/a"
    );
  });

  return {
    invalid: invalidRows.length,
    total: data.length,
    message: `${invalidRows.length} out of ${
      data.length
    } rows have invalid ${getTabDisplayName(
      dataType,
      tabName
    ).toLowerCase()} details`,
  };
};

// Get dropdown options for a specific tab and data type
export const getTabDropdownOptions = (
  dataType: string,
  tabName: string
): { value: string; label: string }[] => {
  const dropdownFunction = tabDropdownOptions[dataType];
  return dropdownFunction ? dropdownFunction(tabName) : [];
};
